import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { saleService, Sale } from '../services/saleService';
import customerService from '../services/customerService';
import { formatCurrency } from '../utils/format';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Calendar, 
  Package, 
  AlertCircle, 
  Clock,
  Eye,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ShoppingBag,
  Clock3,
  CheckCircle2,
  Users,
  ArrowRight,
  X
} from 'lucide-react';
import { SaleDetailModal } from '../components/SaleDetailModal';
import { CustomerBalanceDetail } from '../components/CustomerBalanceDetail';
import toast from 'react-hot-toast';

export const ApartadosPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');
  const urlExpired = searchParams.get('expired') === 'true';
  const urlWarning = searchParams.get('warning') === 'true';

  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 20;

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterModel, setFilterModel] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [filterColor, setFilterColor] = useState('');
  const [filterSeller, setFilterSeller] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>(urlStatus || 'SALDOS'); 
  const [filterExpired, setFilterExpired] = useState<boolean>(urlExpired);
  const [filterWarning, setFilterWarning] = useState<boolean>(urlWarning);
  const [showFilters, setShowFilters] = useState(urlExpired || urlWarning);

  // Estados de Saldos
  const [balances, setBalances] = useState<any[]>([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);

  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  const loadApartados = async (termOverride?: string) => {
    try {
      setLoading(true);
      const searchToUse = termOverride !== undefined ? termOverride : searchTerm;
      const params: any = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        status: filterStatus === 'ALL' ? undefined : filterStatus,
        q: searchToUse || undefined,
        customerName: filterCustomer || undefined,
        marca: filterBrand || undefined,
        modelo: filterModel || undefined,
        talla: filterSize || undefined,
        color: filterColor || undefined,
        pendingOnly: filterStatus === 'ALL' ? 'true' : undefined,
        expiredOnly: filterExpired ? 'true' : undefined,
        warningOnly: filterWarning ? 'true' : undefined,
      };

      const response = await saleService.getSales(params);
      setSales(response.data);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error('Error loading apartados:', error);
      toast.error('Error al cargar apartados');
    } finally {
      setLoading(false);
    }
  };

  const loadBalances = async (termOverride?: string) => {
    try {
      setLoadingBalances(true);
      const searchToUse = termOverride !== undefined ? termOverride : searchTerm;
      const response = await customerService.getCustomersBalances(searchToUse);
      const data = response.data || [];
      setBalances(data);
      
      // Update global count based on total pending notes across all customers
      const totalNotes = data.reduce((sum: number, b: any) => sum + (b.pendingNotesCount || 0), 0);
      setTotalCount(totalNotes);
    } catch (error) {
      console.error('Error loading balances:', error);
      toast.error('Error al cargar balances');
    } finally {
      setLoadingBalances(false);
    }
  };

  useEffect(() => {
    if (filterStatus === 'SALDOS') {
      loadBalances();
    } else {
      loadApartados();
    }
  }, [currentPage, filterStatus, filterExpired, filterWarning]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    if (filterStatus === 'SALDOS') {
      loadBalances();
    } else {
      loadApartados();
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterCustomer('');
    setFilterBrand('');
    setFilterModel('');
    setFilterSize('');
    setFilterColor('');
    setFilterSeller('');
    setFilterStatus('APARTADO');
    setFilterExpired(false);
    setFilterWarning(false);
    setCurrentPage(1);
  };

  const getExpirationStatus = (days: number) => {
    if (days >= 30) return { label: 'Vencido', color: 'text-red-600 bg-red-100', icon: <AlertCircle className="w-4 h-4" /> };
    if (days >= 21) return { label: 'Próximo a vencer', color: 'text-orange-600 bg-orange-100', icon: <Clock className="w-4 h-4" /> };
    return { label: 'Vigente', color: 'text-green-600 bg-green-100', icon: <Calendar className="w-4 h-4" /> };
  };

  const openDetail = (sale: Sale) => {
    setSelectedSale(sale);
    setIsDetailModalOpen(true);
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Control de Pendientes</h1>
            <p className="text-gray-500 mt-1">Gestión centralizada de apartados y pre-orders</p>
          </div>
          <div className="flex gap-4">
            <div className="bg-white border border-purple-100 p-4 rounded-xl shadow-sm flex items-center gap-4 min-w-[200px]">
              <div className="bg-purple-100 p-2.5 rounded-lg text-purple-600">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest leading-none mb-1">
                  {filterStatus === 'COMPLETADA' ? 'Total Completados' : 'Total Pendientes'}
                </p>
                <p className="text-2xl font-black text-gray-900 leading-none">{totalCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6 gap-8">
          {[
            { id: 'SALDOS', label: 'Saldos por Clienta', icon: <Users className="w-4 h-4" /> },
            { id: 'APARTADO', label: 'Apartados', icon: <Package className="w-4 h-4" /> },
            { id: 'PRE-ORDER', label: 'Pre-Orders', icon: <ShoppingBag className="w-4 h-4" /> },
            { id: 'COMPLETADA', label: 'Completados', icon: <CheckCircle2 className="w-4 h-4" /> },
            { id: 'ALL', label: 'Todo Pendiente', icon: <Clock3 className="w-4 h-4" /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setFilterStatus(tab.id);
                setFilterExpired(false);
                setFilterWarning(false);
                setCurrentPage(1);
              }}
              className={`pb-4 px-2 flex items-center gap-2 text-sm font-black transition-all relative whitespace-nowrap ${
                filterStatus === tab.id 
                  ? 'text-purple-600 scale-105' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <div className={`p-1.5 rounded-lg transition-colors ${filterStatus === tab.id ? 'bg-purple-100' : 'bg-transparent'}`}>
                {tab.icon}
              </div>
              <span className="uppercase tracking-tight text-[11px]">{tab.label}</span>
              {filterStatus === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-purple-600 rounded-t-full animate-in slide-in-from-bottom-1 duration-300" />
              )}
            </button>
          ))}
        </div>

        {/* Buscador y Filtros Rápidos */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
              <input
                type="text"
                placeholder={filterStatus === 'SALDOS' ? "Buscar clienta por nombre o teléfono..." : "Buscar por cliente, folio, código o modelo..."}
                className="w-full pl-10 pr-10 py-2.5 border border-purple-100 rounded-xl focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none transition-all text-sm group-hover:border-purple-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                    if (filterStatus === 'SALDOS') loadBalances('');
                    else loadApartados('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-600 p-1 rounded-full hover:bg-purple-50 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="bg-purple-600 text-white px-8 py-2.5 rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-purple-700 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-100"
            >
              <Search className="w-4 h-4" />
              Buscar
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border font-medium flex items-center justify-center gap-2 transition-all ${
                showFilters ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
              title="Limpiar filtros"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
          </form>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Cliente</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={filterCustomer}
                  onChange={(e) => setFilterCustomer(e.target.value)}
                  placeholder="Nombre del cliente"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Marca</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={filterBrand}
                  onChange={(e) => setFilterBrand(e.target.value)}
                  placeholder="Ej: Pronovias"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Modelo / Código</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={filterModel}
                  onChange={(e) => setFilterModel(e.target.value)}
                  placeholder="Ej: ABC-123"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Talla</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={filterSize}
                  onChange={(e) => setFilterSize(e.target.value)}
                  placeholder="Ej: 38"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Color</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={filterColor}
                  onChange={(e) => setFilterColor(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Vendedora</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  value={filterSeller}
                  onChange={(e) => setFilterSeller(e.target.value)}
                />
              </div>
              <div className="md:col-span-2 lg:col-span-2 flex items-center gap-6 self-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${filterExpired ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-gray-300 group-hover:border-red-400'}`}>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={filterExpired}
                      onChange={(e) => {
                        setFilterExpired(e.target.checked);
                        if (e.target.checked) setFilterWarning(false);
                        setCurrentPage(1);
                      }}
                    />
                    {filterExpired && <CheckCircle2 className="w-3.5 h-3.5" />}
                  </div>
                  <span className={`text-sm font-bold ${filterExpired ? 'text-red-600' : 'text-gray-600'}`}>Solo Vencidos</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${filterWarning ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white border-gray-300 group-hover:border-orange-400'}`}>
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={filterWarning}
                      onChange={(e) => {
                        setFilterWarning(e.target.checked);
                        if (e.target.checked) setFilterExpired(false);
                        setCurrentPage(1);
                      }}
                    />
                    {filterWarning && <CheckCircle2 className="w-3.5 h-3.5" />}
                  </div>
                  <span className={`text-sm font-bold ${filterWarning ? 'text-orange-600' : 'text-gray-600'}`}>Próximos a Vencer</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Contenido según Tab */}
        {filterStatus === 'SALDOS' ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase font-bold">
                    <th className="px-6 py-4 border-b">Clienta</th>
                    <th className="px-6 py-4 border-b text-center">Notas Pendientes</th>
                    <th className="px-6 py-4 border-b text-right">Saldo Total</th>
                    <th className="px-6 py-4 border-b text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loadingBalances ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td colSpan={4} className="px-6 py-4 h-16 bg-gray-50/50"></td>
                      </tr>
                    ))
                  ) : balances.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center text-gray-400">
                        No se encontraron clientes con deudas pendientes
                      </td>
                    </tr>
                  ) : (
                    balances.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-gray-900">{row.fullName}</div>
                          <div className="text-xs text-gray-500 tabular-nums">{row.phone || row.email || 'Sin datos de contacto'}</div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center justify-center bg-purple-100 text-purple-700 font-black px-3 py-1 rounded-full text-xs">
                            {row.pendingNotesCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-lg font-black text-red-600 tabular-nums">
                            {formatCurrency(row.totalBalance)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => setSelectedCustomer(row)}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-purple-700 transition-all flex items-center gap-2 mx-auto"
                          >
                            Ver Notas
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <>
            {/* Tabla de Apartados Existente */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-xs uppercase font-bold">
                      <th className="px-3 py-3 border-b">Folio</th>
                      <th className="px-3 py-3 border-b">Fecha</th>
                      <th className="px-3 py-3 border-b">Días</th>
                      <th className="px-3 py-3 border-b">Cliente</th>
                      <th className="px-3 py-3 border-b">Vendedora</th>
                      <th className="px-3 py-3 border-b min-w-[150px]">Prenda</th>
                      <th className="px-3 py-3 border-b">Total</th>
                      <th className="px-3 py-3 border-b">Pagado</th>
                      <th className="px-3 py-3 border-b">Saldo</th>
                      <th className="px-3 py-3 border-b">Estado</th>
                      <th className="px-3 py-3 border-b text-center sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td colSpan={11} className="px-4 py-4 h-12 bg-gray-50/50"></td>
                        </tr>
                      ))
                    ) : sales.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                          No se encontraron apartados con los filtros seleccionados
                        </td>
                      </tr>
                    ) : (
                      sales.map((sale) => {
                        const expiration = getExpirationStatus(sale.dias_transcurridos || 0);
                        const pendingItems = sale.items?.filter(item => {
                            const isDelivered = item.product?.estado_entrega === 'ENTREGADO' || (item.product?.invoice_id && !item.product?.in_transit);
                            return !isDelivered;
                        }) || [];
                        const firstItem = pendingItems[0];
                        
                        return (
                          <tr key={sale.id} className="hover:bg-gray-50 transition-colors group">
                            <td className="px-3 py-3 font-medium text-purple-700 text-sm">{sale.folio || `#${sale.id}`}</td>
                            <td className="px-3 py-3 text-xs text-gray-600">
                              {format(new Date(sale.createdAt), 'dd/MM/yy', { locale: es })}
                            </td>
                            <td className="px-3 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${expiration.color} whitespace-nowrap`}>
                                {expiration.icon}
                                {sale.dias_transcurridos} d
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <div className="font-medium text-gray-900 text-sm truncate max-w-[120px]" title={sale.customerName}>{sale.customerName}</div>
                              <div className="text-[10px] text-gray-500">{sale.customerPhone}</div>
                            </td>
                            <td className="px-3 py-3 text-xs text-gray-600 tabular-nums">{sale.seller?.username || 'Sistema'}</td>
                            <td className="px-3 py-3">
                              {firstItem ? (
                                <div className="text-[10px] max-w-[150px]">
                                  <span className="font-bold text-gray-700 truncate block" title={firstItem.productName}>{firstItem.productName}</span>
                                  <div className="text-gray-500 truncate">
                                    {firstItem.productCode} • {firstItem.product?.talla} • {firstItem.product?.color}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                              {pendingItems.length > 1 && (
                                <span className="text-[9px] bg-gray-100 text-gray-600 px-1 rounded">+{pendingItems.length - 1} más</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-xs font-semibold tabular-nums">{formatCurrency(sale.total)}</td>
                            <td className="px-3 py-3 text-xs text-green-600 tabular-nums">{formatCurrency(sale.amountPaid || 0)}</td>
                            <td className="px-3 py-3 text-xs font-bold text-red-600 tabular-nums">
                              {formatCurrency(sale.total - (sale.amountPaid || 0))}
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex flex-col gap-1">
                                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase whitespace-nowrap ${
                                  sale.status === 'PRE-ORDER' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                                }`}>
                                  {sale.status}
                                </span>
                                {sale.status === 'APARTADO' && (sale as any).isPreOrder && (
                                  <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-600 border border-amber-200 whitespace-nowrap">
                                    Origen: PRE-ORDER
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-center sticky right-0 bg-white group-hover:bg-gray-50 z-10 shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] transition-colors">
                              <div className="flex items-center justify-center gap-1">
                                <button 
                                  onClick={() => openDetail(sale)}
                                  className="p-1 px-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                                  title="Ver detalle"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Paginación */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                <p className="text-sm text-gray-500">
                  Mostrando <span className="font-medium">{sales.length}</span> de <span className="font-medium">{totalCount}</span> productos
                </p>
                {totalCount > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Página</span>
                    <span className="text-sm font-bold text-purple-600">{currentPage}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">de</span>
                    <span className="text-sm font-bold text-gray-600">{Math.ceil(totalCount / itemsPerPage)}</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  disabled={currentPage === 1 || loading}
                  onClick={() => setCurrentPage(1)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 transition-all font-medium flex items-center gap-1 text-sm bg-white hover:text-purple-600 hover:border-purple-200 group"
                  title="Volver al inicio"
                >
                  <ChevronsLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                  <span className="hidden sm:inline">Inicio</span>
                </button>

                <div className="w-px h-6 bg-gray-200 mx-1 hidden sm:block" />

                <button
                  disabled={currentPage === 1 || loading}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 transition-all font-medium flex items-center gap-1 text-sm bg-white hover:text-purple-600 hover:border-purple-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Anterior</span>
                </button>
                
                <button
                  disabled={currentPage * itemsPerPage >= totalCount || loading}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 transition-all font-medium flex items-center gap-1 text-sm bg-white hover:text-purple-600 hover:border-purple-200"
                >
                  <span className="hidden sm:inline">Siguiente</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {isDetailModalOpen && selectedSale && (
        <SaleDetailModal
          onClose={() => setIsDetailModalOpen(false)}
          sale={selectedSale}
          onSaleUpdated={loadApartados}
        />
      )}

      {selectedCustomer && (
        <CustomerBalanceDetail
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onPaymentSuccess={() => {
            loadBalances();
          }}
        />
      )}
    </Layout>
  );
};

export default ApartadosPage;
